import type { ReactNode } from 'react';

import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';

import { FieldRow } from './FieldRow';

export interface MultiToggleOption {
  value: string;
  label: string;
}

export interface MultiToggleFieldProps {
  label: ReactNode;
  description?: ReactNode;
  value: string[];
  options: MultiToggleOption[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  /**
   * Options whose own checkbox is individually disabled (e.g. `/operator` while hand-off is off) —
   * distinct from the field-wide `disabled`. A disabled option's value is neither addable nor
   * removable through the control while disabled, but survives untouched when another option is
   * toggled, exactly like the always-on/unknown values above.
   */
  disabledValues?: readonly string[];
}

/**
 * Fixed-option multi-select rendered as a checkbox group. Used for enum arrays
 * (delivery models, payment methods, notification channels, chatbot commands).
 * Preserves the option order on toggle for a stable payload.
 *
 * Values present in `value` but NOT among `options` are **preserved untouched** (kept first, in
 * their original order). The server may hold values this control deliberately does not expose —
 * the chatbot's always-on `start` command is the canonical case: the hub checks
 * `enabledCommands.contains("start")` on every `/start`, so a control that rebuilt the array from
 * its own options alone silently dropped it on the first click and the bot stopped greeting new
 * customers (defect found 2026-09-18, plan-merchant-portal-019).
 */
export function MultiToggleField({
  label,
  description,
  value,
  options,
  onChange,
  disabled = false,
  disabledValues,
}: MultiToggleFieldProps) {
  const items = value ?? [];
  const disabledValueSet = new Set(disabledValues ?? []);
  const toggle = (optionValue: string, checked: boolean) => {
    const known = new Set(options.map((o) => o.value));
    const preserved = items.filter((v) => !known.has(v));
    if (checked) {
      const next = options.filter((o) => items.includes(o.value) || o.value === optionValue);
      onChange([...preserved, ...next.map((o) => o.value)]);
    } else {
      onChange(items.filter((v) => v !== optionValue));
    }
  };

  return (
    <FieldRow label={label} description={description} alignTop>
      <FormGroup>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                size="small"
                checked={items.includes(option.value)}
                disabled={disabled || disabledValueSet.has(option.value)}
                onChange={(event) => toggle(option.value, event.target.checked)}
              />
            }
            label={option.label}
          />
        ))}
      </FormGroup>
    </FieldRow>
  );
}
